import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useSocket } from "@/app/components/socket/SocketProvider";
import BillingMirrorComponent from "../BillingMirrorComponent";
import type { EmrMirrorCommand, MembershipMirrorCommand, PitchingMirrorCommand } from "../BillingMirrorComponent/types";
import { HomeIcon } from "lucide-react";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";

export interface IMirrorScrrenProps {
  petDetails?: any;
  emrData?: any;
  cartId?: string | null;
  customerId?: string | null;
  onClose?: () => void;
  membershipList?: any;
  pitchingList?: any;
}

const PREVIEW_BASE_WIDTH = 2000;
const PREVIEW_BASE_HEIGHT = 990;

type MirrorTab = "cart" | "emr" | "membership" | "pitching";

const MirrorScreenActions = ({
  petDetails,
  emrData,
  cartId,
  customerId,
  onClose,
  membershipList,
  pitchingList,
}: IMirrorScrrenProps) => {
  const [activeTab, setActiveTab] = useState<MirrorTab>("cart");
  const { sendJsonMessage } = useSocket();
  const mappedClinicId = useAppSelector((state: any) => state.root.UIStates.mappedClinicId);
  const mappedClinicName = useAppSelector((state: any) => state.root.UIStates.mappedClinicName);
  const userLogInfo = useAppSelector((state: any) => state.auth?.user?.email);
  const { execute: logAPI } = useClickLogAPI();

  const logMirrorAction = (eventType: string, extraPayload?: any) => {
    logAPI({
      event_type: eventType as any,
      scr_id: "mirror_centre",
      client_id: customerId, //what to put in client id?
      cart_id: cartId,
      clinic_id: mappedClinicId,
      clinic_name: mappedClinicName,
      vms_user_id: userLogInfo,
      timestamp: Date.now(),
      ...extraPayload,
    });
  };

  useEffect(() => {
    logMirrorAction("MIRROR_CENTER_OPEN");
  }, []);

  const didAutoOpenEmr = useRef(false);
  const [previewEmrCommand, setPreviewEmrCommand] = useState<EmrMirrorCommand | null>(null);
  const [selectedMembershipId, setSelectedMembershipId] = useState<string>();
  const [selectedPitchingId, setSelectedPitchingId] = useState<string>();
  const [previewMembershipCommand, setPreviewMembershipCommand] = useState<MembershipMirrorCommand | null>(null);
  const [previewPitchingCommand, setPreviewPitchingCommand] = useState<PitchingMirrorCommand | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(true);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [previewBounds, setPreviewBounds] = useState({ width: 0, height: 0 });
  const firstEmrId = emrData?.find((e: any) => e?.emr_id)?.emr_id;
  const [selectedEmrId, setSelectedEmrId] = useState<string | undefined>(firstEmrId);

  const openMembership = (userMembershipId: string) => {
    if (!userMembershipId) return;
    setPreviewMembershipCommand({
      action: "OPEN",
      userMembershipId,
      cartId: cartId ?? null,
      ts: Date.now(),
    });
    sendEmrAction({
      action: "MEMBERSHIP_OPEN",
      action_id: userMembershipId,
    });
    logMirrorAction("MEMBERSHIP_OPEN", { membership_id: userMembershipId });
  };

  const openPitching = (membershipId: string) => {
    if (!membershipId) return;
    setPreviewPitchingCommand({
      action: "OPEN",
      membershipId,
      cartId: cartId ?? null,
      ts: Date.now(),
    });
    sendEmrAction({
      action: "MEMBERSHIP_OPEN",
      action_id: membershipId,
    });
    logMirrorAction("PITCHING_OPEN", { membership_id: membershipId });
  };

  const closePitching = () => {
    const closedPitchingId = selectedPitchingId;
    setSelectedPitchingId(undefined);
    setPreviewPitchingCommand({
      action: "CLOSE",
      cartId: cartId ?? null,
      ts: Date.now(),
    });
    sendEmrAction({
      action: "MEMBERSHIP_CLOSE",
      action_id: null,
    });
    if (closedPitchingId) {
      logMirrorAction("PITCHING_CLOSE", { membership_id: closedPitchingId });
    }
  };

  const closeMembership = () => {
    const closedMembershipId = selectedMembershipId;
    if (closedMembershipId) {
      logMirrorAction("MEMBERSHIP_CLOSE", { membership_id: closedMembershipId });
    }
    setSelectedMembershipId(null);
    setPreviewMembershipCommand({
      action: "CLOSE",
      cartId: cartId ?? null,
      ts: Date.now(),
    });
    sendEmrAction({
      action: "MEMBERSHIP_CLOSE",
      action_id: null,
    });
  };

  useEffect(() => {
    if (!Array.isArray(emrData) || emrData.length === 0) {
      setSelectedEmrId(undefined);
      return;
    }
    setSelectedEmrId((prev) => {
      if (prev && emrData.some((entry: any) => String(entry?.emr_id) === String(prev))) return prev;
      return emrData[0]?.emr_id;
    });
  }, [emrData]);

  const sendEmrAction = (payload: Record<string, unknown>) => {
    sendJsonMessage({
      event_type: "EMR_ACTION",
      payload: {
        ...payload,
        cart_id: cartId ?? null,
        customer_id: customerId ?? null,
      },
    });
  };

  const openEmr = (emrId: string) => {
    if (!emrId) return;
    setPreviewEmrCommand({ action: "OPEN", emrId: String(emrId), cartId: cartId ?? null, ts: Date.now() });
    sendEmrAction({
      action: "EMR_OPEN",
      action_id: emrId,
    });

    logMirrorAction("EMR_OPEN", {
      emr_id: emrId,
      ...(petDetails?.high_risk_patient === "High Care" ? { is_high_care: true } : {}),
    });
  };

  const closeEmr = () => {
    setPreviewEmrCommand({ action: "CLOSE", cartId: cartId ?? null, ts: Date.now() });
    sendEmrAction({
      action: "EMR_CLOSE",
      action_id: selectedEmrId,
    });
    logMirrorAction("EMR_CLOSE", {
      emr_id: selectedEmrId,
      ...(petDetails?.high_risk_patient === "High Care" ? { is_high_care: true } : {}),
    });
  };

  useEffect(() => {
    if (!selectedEmrId) return;
    if (petDetails?.high_risk_patient === "High Care" && !didAutoOpenEmr.current) {
      didAutoOpenEmr.current = true;
      setActiveTab("emr");
      openEmr(selectedEmrId);
    }
  }, [petDetails?.high_risk_patient, selectedEmrId, cartId, customerId]);

  useEffect(() => {
    if (!isPreviewOpen) return;
    const container = previewRef.current;
    if (!container) return;

    const update = () => {
      const rect = container.getBoundingClientRect();
      setPreviewBounds({ width: rect.width, height: rect.height });
    };
    update();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [isPreviewOpen]);

  const previewScale = useMemo(() => {
    if (!previewBounds.width || !previewBounds.height) return 0;
    return Math.min(1, previewBounds.width / PREVIEW_BASE_WIDTH, previewBounds.height / PREVIEW_BASE_HEIGHT);
  }, [previewBounds.height, previewBounds.width]);

  const previewOffsets = useMemo(() => {
    if (!previewScale) return { x: 0, y: 0 };
    return {
      x: Math.max(0, (previewBounds.width - PREVIEW_BASE_WIDTH * previewScale) / 2),
      y: Math.max(0, (previewBounds.height - PREVIEW_BASE_HEIGHT * previewScale) / 2),
    };
  }, [previewBounds.height, previewBounds.width, previewScale]);

  const handleClickTab = (tab: MirrorTab) => {
    logMirrorAction("MIRROR_TAB_CLICK", { cta_text: tab });
    setActiveTab(tab);
    if (tab !== "pitching" && selectedPitchingId) closePitching();
    if (tab === "cart") {
      closeEmr();
      closeMembership();
    }

    if (tab === "emr") {
      closeMembership();
      if (selectedEmrId) openEmr(selectedEmrId);
    } else if (tab === "membership") {
      closeEmr();
    } else {
      closeEmr();
      closeMembership();
    }
  };

  const handleScrollCart = (direction: "up" | "down") => {
    if (!cartId) return;
    const delta = direction === "up" ? -240 : 240;
    sendEmrAction({
      action: "CART_SCROLL_STEP",
      direction,
      delta,
    });
    const previewContainer = previewRef.current;
    const previewScrollTarget = previewContainer?.querySelector<HTMLElement>('[data-mirror-cart-scroll="true"]');
    previewScrollTarget?.scrollBy({ top: delta, behavior: "smooth" });
    logMirrorAction("CART_SCROLL");
  };

  const handleCloseEmr = () => {
    setActiveTab(undefined);
    closeEmr();
  };

  const handleScrollEmr = (direction: "up" | "down") => {
    if (!activeTab || !selectedEmrId) return;
    const delta = direction === "up" ? -240 : 240;
    sendEmrAction({
      action: "EMR_SCROLL_STEP",
      action_id: selectedEmrId,
      direction,
      delta,
    });
    logMirrorAction("EMR_SCROLL", { emr_id: selectedEmrId });
  };

  const handleScrollMembership = (direction: "up" | "down") => {
    const delta = direction === "up" ? -240 : 240;
    const openDetailId = selectedMembershipId ?? selectedPitchingId;

    sendEmrAction({
      action: "MEMBERSHIP_SCROLL",
      action_id: openDetailId ?? null,
      direction,
      delta,
    });

    const previewContainer = previewRef.current;
    const scrollTarget = previewContainer?.querySelector(
      openDetailId ? '[data-mirror-membership-detail-scroll="true"]' : '[data-mirror-membership-scroll="true"]',
    );
    scrollTarget?.scrollBy({ top: delta, behavior: "smooth" });

    logMirrorAction(activeTab === "pitching" ? "PITCHING_SCROLL" : "MEMBERSHIP_SCROLL", {
      membership_id: openDetailId ?? "",
    });
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (["ArrowUp", "ArrowDown"].includes(e.key)) {
        e.preventDefault();
      }
      if (activeTab === "cart") {
        if (e.key === "ArrowDown") handleScrollCart("down");
        if (e.key === "ArrowUp") handleScrollCart("up");
      }
      if (activeTab === "emr") {
        if (e.key === "ArrowDown") handleScrollEmr("down");
        if (e.key === "ArrowUp") handleScrollEmr("up");
      }
      if (activeTab === "membership" || activeTab === "pitching") {
        if (e.key === "ArrowDown") handleScrollMembership("down");
        if (e.key === "ArrowUp") handleScrollMembership("up");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeTab, selectedEmrId, selectedMembershipId, selectedPitchingId]);

  const buttonBase =
    "rounded-full border px-3 py-1 text-xs font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2";

  return (
    <div className="fixed z-50 flex h-[80vh] w-[135vh] flex-col overflow-hidden rounded-xl border border-border/80 bg-white shadow-xl ">
      <div className="flex items-center justify-between rounded-t-xl px-3 py-3" aria-label="Drag mirror screen panel">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => handleClickTab("cart")}
            onPointerDown={(event) => event.stopPropagation()}
            className={`${buttonBase} ${
              activeTab === "cart"
                ? "border-blue-600 bg-blue-600 text-white"
                : "border-blue-200 bg-blue-50 text-blue-700"
            }`}
          >
            <div className="flex gap-1">
              {" "}
              <HomeIcon size={14} /> Cart
            </div>
          </button>
          <button
            type="button"
            onClick={() => handleClickTab("emr")}
            onPointerDown={(event) => event.stopPropagation()}
            className={`${buttonBase} ${
              activeTab === "emr"
                ? "border-blue-600 bg-blue-600 text-white"
                : "border-blue-200 bg-blue-50 text-blue-700"
            }`}
          >
            EMR
          </button>
          <button
            type="button"
            onClick={() => handleClickTab("membership")}
            onPointerDown={(event) => event.stopPropagation()}
            className={`${buttonBase} ${
              activeTab === "membership"
                ? "border-blue-600 bg-blue-600 text-white"
                : "border-blue-200 bg-blue-50 text-blue-700"
            }`}
          >
            Membership Utilization
          </button>
          <button
            type="button"
            onClick={() => handleClickTab("pitching")}
            onPointerDown={(event) => event.stopPropagation()}
            className={`${buttonBase} ${
              activeTab === "pitching"
                ? "border-blue-600 bg-blue-600 text-white"
                : "border-blue-200 bg-blue-50 text-blue-700"
            }`}
          >
            Membership Pitching
          </button>
        </div>
        <div className="flex items-center gap-2">
          {activeTab === "cart" && (
            <>
              {activeTab === "cart" && (
                <button
                  onClick={() => handleScrollCart("up")}
                  className="h-9 w-9 flex items-center justify-center rounded-lg 
    border border-blue-200 bg-white text-blue-600 shadow-sm
    hover:bg-blue-50 hover:shadow-md active:scale-95 transition"
                >
                  <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12.5l5-5 5 5" />
                  </svg>
                </button>
              )}
              {activeTab === "cart" && (
                <button
                  onClick={() => handleScrollCart("down")}
                  className="h-9 w-9 flex items-center justify-center rounded-lg 
    border border-blue-200 bg-white text-blue-600 shadow-sm
    hover:bg-blue-50 hover:shadow-md active:scale-95 transition"
                >
                  <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 7.5l5 5 5-5" />
                  </svg>
                </button>
              )}
            </>
          )}
          {activeTab === "emr" && (
            <>
              <button
                type="button"
                onClick={() => handleScrollEmr("up")}
                className="h-9 w-9 flex items-center justify-center rounded-lg 
    border border-blue-200 bg-white text-blue-600 shadow-sm
    hover:bg-blue-50 hover:shadow-md active:scale-95 transition"
                aria-label="Scroll EMR Up"
              >
                <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12.5l5-5 5 5" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => handleScrollEmr("down")}
                className="h-9 w-9 flex items-center justify-center rounded-lg 
    border border-blue-200 bg-white text-blue-600 shadow-sm
    hover:bg-blue-50 hover:shadow-md active:scale-95 transition"
                aria-label="Scroll EMR Down"
              >
                <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 7.5l5 5 5-5" />
                </svg>
              </button>
            </>
          )}
          {(activeTab === "membership" || activeTab === "pitching") && (
            <>
              <button
                type="button"
                onClick={() => handleScrollMembership("up")}
                className="h-9 w-9 flex items-center justify-center rounded-lg 
    border border-blue-200 bg-white text-blue-600 shadow-sm
    hover:bg-blue-50 hover:shadow-md active:scale-95 transition"
                aria-label="Scroll EMR Up"
              >
                <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12.5l5-5 5 5" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => handleScrollMembership("down")}
                className="h-9 w-9 flex items-center justify-center rounded-lg 
    border border-blue-200 bg-white text-blue-600 shadow-sm
    hover:bg-blue-50 hover:shadow-md active:scale-95 transition"
                aria-label="Scroll EMR Down"
              >
                <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 7.5l5 5 5-5" />
                </svg>
              </button>
            </>
          )}
          {onClose && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                logMirrorAction("MIRROR_CENTER_CLOSE");
                onClose?.();
                setIsPreviewOpen(false);
                handleCloseEmr();
                closeMembership();
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg 
  border text-red-600 transition"
              aria-label="Close"
            >
              <svg
                viewBox="0 0 20 20"
                aria-hidden="true"
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 5l10 10M15 5l-10 10" />
              </svg>
            </button>
          )}
        </div>
      </div>
      <div className="flex-1 p-2 w-full rounded-b-xl bg-white border-t border-border/80 bg-gradient-to-br from-background via-background to-muted/20 overflow-hidden">
        <div className="grid h-full grid-rows-[auto_1fr] gap-2 min-h-0">
          <div className="min-h-0 overflow-hidden">
            {activeTab === "emr" && (
              <div className="flex flex-col">
                <div className="flex items-center gap-2 px-3 py-2 bg-white/80">
                  <div className=" flex-1 overflow-x-auto">
                    {emrData?.length > 0 ? (
                      <div className="flex gap-2">
                        {emrData.map((e: any) => (
                          <button
                            key={e.emr_id}
                            onClick={() => {
                              setSelectedEmrId(e.emr_id);
                              openEmr(e.emr_id);
                            }}
                            className={`text-left px-3 py-2 rounded-md border text-[11px]
            transition-all ${
              selectedEmrId === e.emr_id
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-blue-50 text-blue-700 border-blue-200"
            }`}
                          >
                            {e.pet_name}
                            <span className="p-1">EMR -</span>
                            {e.index}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[11px]">No Complete EMR Available</p>
                    )}
                  </div>
                </div>
              </div>
            )}
            {activeTab === "membership" && (
              <div className="flex flex-col">
                <div className="flex items-center px-3 py-0 bg-white/80">
                  <div className="flex-1 overflow-x-auto">
                    {membershipList.length > 0 ? (
                      <div className="flex gap-2">
                        {membershipList.map((m: any) => {
                          return (
                            <button
                              key={m.user_membership_id}
                              onClick={() => {
                                setSelectedMembershipId(m.user_membership_id);
                                openMembership(m.user_membership_id);
                              }}
                              className={`text-left px-3 py-2 rounded-md border text-[11px]
            transition-all ${
              selectedMembershipId === m.user_membership_id
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-blue-50 text-blue-700 border-blue-200"
            }`}
                            >
                              <div className="font-medium truncate">{m.category}</div>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-[11px]">This user is not enrolled in any membership plan.</p>
                    )}
                  </div>
                </div>
              </div>
            )}
            {activeTab === "pitching" && (
              <div className="flex flex-col">
                <div className="flex items-center px-3 py-0 bg-white/80">
                  <div className="flex-1 overflow-x-auto">
                    {pitchingList?.length > 0 ? (
                      <div className="flex gap-2">
                        {pitchingList.map((p: any) => (
                          <button
                            key={p.membership_id}
                            onClick={() => {
                              setSelectedPitchingId(p.membership_id);
                              openPitching(p.membership_id);
                            }}
                            className={`text-left px-3 py-2 rounded-md border text-[11px]
            transition-all ${
              selectedPitchingId === p.membership_id
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-blue-50 text-blue-700 border-blue-200"
            }`}
                          >
                            <div className="font-medium truncate">{p.title}</div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[11px]">No membership plans to pitch for this cart.</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="min-h-0 rounded border bg-white shadow-sm overflow-hidden">
            {isPreviewOpen && (
              <div ref={previewRef} className="relative h-full w-full overflow-hidden border-t bg-gray-100">
                {previewScale > 0 && (
                  <div
                    className="pointer-events-none absolute"
                    style={
                      {
                        width: PREVIEW_BASE_WIDTH,
                        height: PREVIEW_BASE_HEIGHT,
                        left: previewOffsets.x,
                        top: previewOffsets.y,
                        transform: `scale(${previewScale})`,
                        transformOrigin: "top left",
                        willChange: "transform",
                        ["--mirror-frame-height" as any]: `${PREVIEW_BASE_HEIGHT}px`,
                        ["--mirror-frame-width" as any]: `${PREVIEW_BASE_WIDTH}px`,
                      } as CSSProperties
                    }
                  >
                    <BillingMirrorComponent
                      embedded
                      compact={false}
                      source="ACTION"
                      emrCommand={previewEmrCommand}
                      membershipCommand={previewMembershipCommand}
                      pitchingCommand={previewPitchingCommand}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MirrorScreenActions;
